"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import { useWaveDropsSearch } from "@/hooks/useWaveDropsSearch";
import { ChevronLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FocusTrap } from "focus-trap-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import Drop, { DropLocation } from "../Drop";
const MIN_QUERY_LENGTH = 2;
const normalize = (value: string) => value.trim();
export default function WaveDropsSearchModal(t0) {
  const $ = _c(86);
  const {
    isOpen,
    onClose,
    wave,
    onSelectSerialNo
  } = t0;
  const modalRef = useRef(null);
  let t1;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t1 = () => {
      if (isOpen) {
        onClose();
      }
    };
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useClickAway(modalRef, t1);
  let t2;
  if ($[3] !== isOpen || $[4] !== onClose) {
    t2 = () => {
      if (isOpen) {
        onClose();
      }
    };
    $[3] = isOpen;
    $[4] = onClose;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useKeyPressEvent("Escape", t2);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  let t3;
  if ($[6] !== query) {
    t3 = () => setDebouncedQuery(query);
    $[6] = query;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== query) {
    t4 = [query];
    $[8] = query;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useDebounce(t3, 250, t4);
  let t5;
  let t6;
  if ($[10] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        setQuery("");
        setDebouncedQuery("");
      }
    };
    t6 = [isOpen];
    $[10] = isOpen;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== debouncedQuery) {
    t7 = normalize(debouncedQuery);
    $[13] = debouncedQuery;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const normalizedQuery = t7;
  const meetsMinLength = normalizedQuery.length >= MIN_QUERY_LENGTH;
  let t8;
  if ($[15] !== wave) {
    t8 = {
      wave
    };
    $[15] = wave;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const {
    winningThreshold,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t8);
  const t9 = isOpen && meetsMinLength;
  let t10;
  if ($[17] !== normalizedQuery || $[18] !== t9 || $[19] !== wave) {
    t10 = {
      wave,
      term: normalizedQuery,
      enabled: t9,
      size: 50
    };
    $[17] = normalizedQuery;
    $[18] = t9;
    $[19] = wave;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const {
    drops: results,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useWaveDropsSearch(t10);
  const inputRef = useRef(null);
  let t11;
  let t12;
  if ($[21] !== isOpen) {
    t11 = () => {
      if (!isOpen) {
        return;
      }
      const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    };
    t12 = [isOpen];
    $[21] = isOpen;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  useEffect(t11, t12);
  if (!isOpen || typeof document === "undefined") {
    return null;
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      allowOutsideClick: true,
      fallbackFocus: () => modalRef.current as HTMLElement | null ?? inputRef.current as HTMLElement | null ?? document.body,
      initialFocus: () => inputRef.current as HTMLElement | null ?? modalRef.current as HTMLElement | null ?? document.body
    };
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="tw-fixed tw-inset-0 tw-bg-gray-600 tw-bg-opacity-50 tw-backdrop-blur-[1px]" />;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ChevronLeftIcon className="tw-size-6 tw-flex-shrink-0" />;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== onClose) {
    t16 = <button type="button" onClick={onClose} aria-label="Close search" className="-tw-ml-1 tw-mr-1 tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-transparent tw-text-iron-300 tw-transition tw-duration-150 hover:tw-text-white sm:tw-hidden">{t15}</button>;
    $[27] = onClose;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="tw-text-xs tw-tracking-wide tw-text-iron-400">Search in</div>;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== wave.name) {
    t18 = <div className="tw-min-w-0 tw-flex-1">{t17}<div className="tw-truncate tw-text-sm tw-font-semibold tw-text-iron-50">{wave.name}</div></div>;
    $[30] = wave.name;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <XMarkIcon className="tw-size-5 tw-flex-shrink-0" />;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== onClose) {
    t20 = <button type="button" onClick={onClose} aria-label="Close search" className="tw-hidden tw-size-9 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white sm:tw-inline-flex">{t19}</button>;
    $[33] = onClose;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t16 || $[36] !== t18 || $[37] !== t20) {
    t21 = <div className="tw-flex tw-items-center tw-gap-2 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/10 tw-px-4 tw-py-4">{t16}{t18}{t20}</div>;
    $[35] = t16;
    $[36] = t18;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <svg className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-3.5 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>;
    t23 = <label className="tw-sr-only" htmlFor="wave-drops-search-input">Search messages</label>;
    $[39] = t22;
    $[40] = t23;
  } else {
    t22 = $[39];
    t23 = $[40];
  }
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e => setQuery(e.target.value);
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== query) {
    t25 = <input id="wave-drops-search-input" ref={inputRef} type="text" autoComplete="off" value={query} onChange={t24} className="sm:text-sm tw-form-input tw-block tw-w-full tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-py-3 tw-pl-11 tw-pr-10 tw-text-base tw-font-normal tw-text-iron-50 tw-caret-primary-300 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 hover:tw-ring-iron-600 focus:tw-bg-transparent focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-300" placeholder="Search messages" />;
    $[42] = query;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== query.length) {
    t26 = query.length > 0 && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="tw-absolute tw-right-2 tw-top-1/2 tw-flex tw-size-8 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-iron-400 tw-transition tw-duration-150 hover:tw-text-iron-100"><XMarkIcon className="tw-size-5 tw-flex-shrink-0" /></button>;
    $[44] = query.length;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t25 || $[47] !== t26) {
    t27 = <div className="tw-px-4 tw-pb-4"><div className="tw-relative">{t22}{t23}{t25}{t26}</div></div>;
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== isLoading) {
    t28 = isLoading && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-300">Loading…</div>;
    $[49] = isLoading;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  let t29;
  if ($[51] !== isError || $[52] !== isLoading) {
    t29 = !isLoading && isError && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-iron-300">Couldn’t load drops for search.</div>;
    $[51] = isError;
    $[52] = isLoading;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  let t30;
  if ($[54] !== isError || $[55] !== isLoading || $[56] !== meetsMinLength) {
    t30 = !isLoading && !isError && !meetsMinLength && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-sm tw-text-iron-400">Type at least {MIN_QUERY_LENGTH} characters to search.</div>;
    $[54] = isError;
    $[55] = isLoading;
    $[56] = meetsMinLength;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== isError || $[59] !== isLoading || $[60] !== meetsMinLength || $[61] !== results) {
    t31 = !isLoading && !isError && meetsMinLength && results.length === 0 && <div className="tw-flex tw-items-center tw-justify-center tw-py-10 tw-text-sm tw-text-iron-400">No matches found.</div>;
    $[58] = isError;
    $[59] = isLoading;
    $[60] = meetsMinLength;
    $[61] = results;
    $[62] = t31;
  } else {
    t31 = $[62];
  }
  let t32;
  if ($[63] !== fetchNextPage || $[64] !== hasNextPage || $[65] !== isError || $[66] !== isFetchingNextPage || $[67] !== isLoading || $[68] !== isVotingClosed || $[69] !== isVotingControlsLocked || $[70] !== meetsMinLength || $[71] !== onClose || $[72] !== onSelectSerialNo || $[73] !== results || $[74] !== winningThreshold) {
    t32 = !isLoading && !isError && meetsMinLength && results.length > 0 && <div className="tw-space-y-2"><div className="tw-text-xs tw-text-iron-400">{results.length} result{results.length === 1 ? "" : "s"}</div><div className="tw-space-y-2">{results.map((drop, index) => {
          const previousDrop = results[index - 1] ?? null;
          const nextDrop = results[index + 1] ?? null;
          const serialNo = drop.serial_no;
          const canSelect = typeof serialNo === "number";
          return <button type="button" key={drop.stableKey} disabled={!canSelect} onClick={() => {
            if (!canSelect) {
              return;
            }
            onSelectSerialNo(serialNo);
            onClose();
          }} className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950/50 tw-text-left tw-transition tw-duration-150 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-border-iron-600 desktop-hover:hover:tw-bg-iron-900/40"><div className="tw-pointer-events-none"><Drop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={_temp} onReplyClick={_temp2} onQuoteClick={_temp3} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></div></button>;
        })}</div>{hasNextPage && <div className="tw-flex tw-justify-center tw-pt-2"><button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white disabled:tw-cursor-not-allowed disabled:tw-opacity-50">{isFetchingNextPage ? "Loading\u2026" : "Load more"}</button></div>}</div>;
    $[63] = fetchNextPage;
    $[64] = hasNextPage;
    $[65] = isError;
    $[66] = isFetchingNextPage;
    $[67] = isLoading;
    $[68] = isVotingClosed;
    $[69] = isVotingControlsLocked;
    $[70] = meetsMinLength;
    $[71] = onClose;
    $[72] = onSelectSerialNo;
    $[73] = results;
    $[74] = winningThreshold;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t28 || $[77] !== t29 || $[78] !== t30 || $[79] !== t31 || $[80] !== t32) {
    t33 = <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-4 tw-pb-6">{t28}{t29}{t30}{t31}{t32}</div>;
    $[76] = t28;
    $[77] = t29;
    $[78] = t30;
    $[79] = t31;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t21 || $[83] !== t27 || $[84] !== t33) {
    t34 = createPortal(<FocusTrap focusTrapOptions={t13}><div className="tailwind-scope tw-relative tw-z-1000 tw-cursor-default">{t14}<div className="tw-fixed tw-inset-0 tw-z-1000 tw-overflow-y-auto"><div className="tw-flex tw-min-h-full tw-items-start tw-justify-center tw-p-0 tw-text-center sm:tw-items-center sm:tw-p-6"><div ref={modalRef} aria-modal="true" aria-labelledby="wave-drops-search-input" className="inset-safe-area tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-[min(100vw,900px)] tw-transform tw-flex-col tw-overflow-hidden tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-text-left tw-shadow-xl tw-transition-all sm:tw-h-[70vh] sm:tw-rounded-xl">{t21}{t27}{t33}</div></div></div></div></FocusTrap>, document.body);
    $[82] = t21;
    $[83] = t27;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  return t34;
}
function _temp3() {}
function _temp2() {}
function _temp() {}